import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Eye, Edit, Trash2, Search } from 'lucide-react';
import { format } from 'date-fns';

interface TipoProposta {
  id: string;
  nome: string;
}

interface ModeloProposta {
  id: string;
  nome_modelo: string;
  tipo_proposta_id: string;
  conteudo_html: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  tipo_proposta?: TipoProposta;
}

export default function ModelosProposta() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission, refetch } = usePermissions();
  
  const [modelos, setModelos] = useState<ModeloProposta[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [permissionsChecked, setPermissionsChecked] = useState(false);

  const canRead = hasPermission('recursos_humanos', 'modelos_proposta', 'read');
  const canWrite = hasPermission('recursos_humanos', 'modelos_proposta', 'write');
  const canAdmin = hasPermission('recursos_humanos', 'modelos_proposta', 'admin');

  useEffect(() => {
    refetch().then(() => {
      setPermissionsChecked(true);
    });
  }, []);

  useEffect(() => {
    if (!permissionsChecked) return;
    if (!canRead) {
      navigate('/intranet');
      return;
    }
    fetchModelos();
  }, [canRead, permissionsChecked]);

  const fetchModelos = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('modelo_proposta')
        .select(`
          *,
          tipo_proposta:tipo_proposta_id (
            id,
            nome
          )
        `)
        .eq('status', 'ativo');

      if (searchTerm) {
        query = query.ilike('nome_modelo', `%${searchTerm}%`);
      }

      query = query.order('nome_modelo', { ascending: true });
      
      const { data, error } = await query;
      if (error) throw error;
      
      setModelos(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar modelos de proposta",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchModelos();
  };

  const handleDelete = async (id: string) => {
    if (!canAdmin) {
      toast({
        title: "Acesso negado",
        description: "Você não tem permissão para excluir modelos de proposta.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('modelo_proposta')
        .update({ 
          status: 'inativo',
          updated_by: user?.id
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Modelo de proposta excluído com sucesso!",
      });

      fetchModelos();
    } catch (error: any) {
      toast({
        title: "Erro ao excluir modelo de proposta",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const filteredModelos = modelos.filter(modelo =>
    modelo.nome_modelo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!permissionsChecked) {
    return (
      <div className="p-6">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="p-6">
        <Card className="p-6 text-center">
          <h2 className="text-xl font-semibold mb-2">Acesso Negado</h2>
          <p className="text-muted-foreground">
            Você não tem permissão para visualizar modelos de proposta.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Modelos de Proposta</h1>
          <p className="text-muted-foreground">Gerencie os modelos de proposta do sistema</p>
        </div>
        
        {canWrite && (
          <Button onClick={() => navigate('/intranet/modelos-proposta/novo')}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Modelo
          </Button>
        )}
      </div>

      <Card className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Input
            placeholder="Pesquisar modelos de proposta..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="max-w-sm"
          />
          <Button onClick={handleSearch}>
            <Search className="h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : filteredModelos.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {searchTerm ? 'Nenhum resultado encontrado.' : 'Nenhum modelo de proposta cadastrado.'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome do Modelo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Data de Criação</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredModelos.map((modelo) => (
                <TableRow key={modelo.id}>
                  <TableCell className="font-medium">{modelo.nome_modelo}</TableCell>
                  <TableCell>{modelo.tipo_proposta?.nome || 'N/A'}</TableCell>
                  <TableCell>
                    <Badge variant={modelo.status === 'ativo' ? "default" : "secondary"}>
                      {modelo.status === 'ativo' ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>{format(new Date(modelo.created_at), 'dd/MM/yyyy')}</TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/intranet/modelos-proposta/${modelo.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      
                      {canWrite && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/intranet/modelos-proposta/${modelo.id}/editar`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      
                      {canAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (window.confirm(`Tem certeza que deseja excluir o modelo "${modelo.nome_modelo}"?`)) {
                              handleDelete(modelo.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}